import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import NameInputBox from 'components/common/NameInputBox';
import CustomSelect from 'components/common/CustomSelect';
import CustomDatePicker from 'components/common/CustomDatePicker';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { GENDER_OPTIONS } from 'constants';
import { createMyAthlete, updateMyAthlete } from 'api/userApi';

const EMPTY = { firstName: '', lastName: '', gender: '', birthday: '' };

// Add or edit one of the caller's own athletes — IDENTITY ONLY (personal-portal §4.6). Club data
// (membership, bib, expiry, age group) is read-only and not touched here. Server validates on submit.
const AthleteEditModal = ({ isOpen, athlete, onClose, onSaved }) => {
  const isEdit = !!athlete;
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setValues(
        athlete
          ? {
              firstName: athlete.firstName || '',
              lastName: athlete.lastName || '',
              gender: athlete.gender || '',
              birthday: athlete.birthDate || '',
            }
          : EMPTY
      );
      setErrors({});
    }
  }, [isOpen, athlete]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const close = () => {
    setValues(EMPTY);
    setErrors({});
    onClose();
  };

  const submit = async () => {
    const next = {};
    if (!values.firstName.trim()) next.firstName = 'First name is required';
    if (!values.lastName.trim()) next.lastName = 'Last name is required';
    if (!values.gender) next.gender = 'Gender is required';
    if (!values.birthday) next.birthday = 'Date of birth is required';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const payload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      gender: values.gender,
      birthDate: values.birthday,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await updateMyAthlete(athlete.athleteProfileId, payload);
      } else {
        await createMyAthlete(payload);
      }
      toast.success(isEdit ? 'Athlete updated.' : 'Athlete added.');
      onSaved();
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
          e.response?.data ||
          'Could not save the athlete.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomNewConfirm
      isOpen={isOpen}
      title={isEdit ? 'Edit athlete' : 'Add athlete'}
      confirmButtonText={saving ? 'Saving…' : 'Save'}
      confirmButtonVariant="primary"
      cancelButtonText="Cancel"
      isLoading={saving}
      onConfirm={submit}
      onClose={close}
    >
      <div>
        <NameInputBox
          label="Athlete Name"
          formValues={values}
          errors={errors}
          handleInputChange={handleChange}
          style={{ marginBottom: 16 }}
        />
        <div className="twoColumnRow">
          <div className="flex1">
            <CustomSelect
              label="Gender"
              name="gender"
              value={values.gender}
              onChange={handleChange}
              options={GENDER_OPTIONS}
              error={errors.gender}
            />
          </div>
          <div className="flex1">
            <CustomDatePicker
              label="Date of Birth"
              name="birthday"
              value={values.birthday}
              onChange={handleChange}
              error={errors.birthday}
            />
          </div>
        </div>
      </div>
    </CustomNewConfirm>
  );
};

AthleteEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  athlete: PropTypes.object, // null/undefined = add mode
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default AthleteEditModal;
